import { Button } from '@recipes/ui';
import { ExternalLink, Play } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { youtubeId } from './video-links';

/**
 * A recipe's video (user, 2026-09-28): a YouTube link becomes a player that loads only after a
 * click – nothing reaches YouTube before – any other link is a link. `title` names the frame.
 */
export function RecipeVideo({ url, title }: { url: string; title: string }) {
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(false);
  const id = youtubeId(url);
  if (!id) {
    return (
      <div className="rounded-lg border bg-muted/40 p-3 text-sm" data-testid="recipe-video">
        <p className="font-medium">{t('recipes.video.title')}</p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="mt-1 inline-flex items-center gap-1 break-all text-primary underline-offset-4 hover:underline"
        >
          {url}
          <ExternalLink className="size-3.5 shrink-0" aria-hidden />
          <span className="sr-only">({t('recipes.video.opensNewTab')})</span>
        </a>
      </div>
    );
  }
  return (
    <section aria-labelledby="video-heading" className="space-y-2" data-testid="recipe-video">
      <h2 id="video-heading" className="text-xl font-semibold">
        {t('recipes.video.title')}
      </h2>
      <div className="relative aspect-video w-full overflow-hidden rounded-lg border bg-muted">
        {playing ? (
          <iframe
            className="absolute inset-0 size-full"
            src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            data-testid="recipe-video-frame"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4 text-center">
            <Button
              type="button"
              size="lg"
              onClick={() => setPlaying(true)}
              data-testid="recipe-video-play"
            >
              <Play className="size-5" aria-hidden />
              {t('recipes.video.play')}
            </Button>
            <p className="text-xs text-muted-foreground">{t('recipes.video.consent')}</p>
          </div>
        )}
      </div>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="inline-flex items-center gap-1 text-sm text-primary underline-offset-4 hover:underline"
      >
        {t('recipes.video.openOnYouTube')}
        <ExternalLink className="size-3.5 shrink-0" aria-hidden />
        <span className="sr-only">({t('recipes.video.opensNewTab')})</span>
      </a>
    </section>
  );
}
